import { useCallback, useRef, useState } from 'react'
import { formatSize } from '@/utils/format'
import { validateFile, MAX_FILE_SIZE, ALLOWED_CONTENT_TYPES, ALLOWED_EXTENSIONS } from '@/utils/file-validation'
import { Button } from '@/shared/components/atoms/ui/button'
import { Icon } from '@/shared/components/atoms/Icon'
import { useUploadFile } from '../../hooks/useFiles'

interface FileUploadProps {
	entityType?: string
	entityId?: string
	isPublic?: boolean
	onUploadComplete?: () => void
}

export function FileUpload({ entityType, entityId, isPublic, onUploadComplete }: FileUploadProps) {
	const [isDragOver, setIsDragOver] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [success, setSuccess] = useState(false)
	const inputRef = useRef<HTMLInputElement>(null)
	const uploadMutation = useUploadFile()

	const handleFile = useCallback(
		async (file: File) => {
			setError(null)
			setSuccess(false)

			const validationError = validateFile(file)
			if (validationError) {
				setError(validationError)
				return
			}

			try {
				await uploadMutation.mutateAsync({ file, entityType, entityId, isPublic })
				setSuccess(true)
				onUploadComplete?.()
				setTimeout(() => setSuccess(false), 3000)
			} catch (err) {
				setError(err instanceof Error ? err.message : 'Upload failed')
			}
		},
		[entityType, entityId, isPublic, uploadMutation, onUploadComplete],
	)

	const handleDrop = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault()
			setIsDragOver(false)
			const file = e.dataTransfer.files[0]
			if (file) void handleFile(file)
		},
		[handleFile],
	)

	const handleDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault()
		setIsDragOver(true)
	}, [])

	const handleDragLeave = useCallback((e: React.DragEvent) => {
		e.preventDefault()
		setIsDragOver(false)
	}, [])

	const handleInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0]
			if (file) void handleFile(file)
			if (inputRef.current) inputRef.current.value = ''
		},
		[handleFile],
	)

	return (
		<div className="w-full">
			<div
				data-testid="file-upload-zone"
				onDrop={handleDrop}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				className={`flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-8 transition-colors ${
					isDragOver ? 'border-blue-500 bg-blue-50' : 'border-border bg-muted hover:border-muted-foreground'
				}`}
			>
				{uploadMutation.isPending ? (
					<>
						<Icon name="LoaderCircle" size={32} className="animate-spin text-blue-500" />
						<p className="text-sm text-muted-foreground">Uploading...</p>
					</>
				) : success ? (
					<>
						<Icon name="CircleCheck" size={32} className="text-green-500" />
						<p className="text-sm text-green-600">Upload complete!</p>
					</>
				) : (
					<>
						<Icon name="Upload" size={32} className="text-muted-foreground" />
						<p className="text-sm text-muted-foreground">Drag and drop a file, or click to browse</p>
						<p className="text-xs text-muted-foreground">
							Max {formatSize(MAX_FILE_SIZE)}. {ALLOWED_EXTENSIONS}
						</p>
						<Button variant="outline" size="sm" onClick={() => inputRef.current?.click()} type="button">
							Browse files
						</Button>
					</>
				)}
			</div>

			<input
				ref={inputRef}
				type="file"
				className="hidden"
				onChange={handleInputChange}
				accept={ALLOWED_CONTENT_TYPES.join(',')}
				data-testid="file-upload-input"
			/>

			{error && (
				<div className="mt-2 flex items-center gap-2 rounded-md bg-red-50 p-3 text-sm text-red-700">
					<Icon name="CircleAlert" size="sm" className="flex-shrink-0" />
					<span>{error}</span>
					<button type="button" onClick={() => setError(null)} className="ml-auto" aria-label="Dismiss error">
						<Icon name="X" size="sm" />
					</button>
				</div>
			)}
		</div>
	)
}
